import { useState } from 'react';
import {
  cartLineKey,
  type CartLine,
  type OeSealCartLine,
  type SelectedPosition,
} from '../api/types.js';
import { GuidedSelection } from '../components/GuidedSelection.js';
import { FreeTextSearch } from '../components/FreeTextSearch.js';
import { OeConfigurator } from '../components/OeConfigurator.js';
import { SelectionList } from '../components/SelectionList.js';
import { QuotePanel } from '../components/QuotePanel.js';
import { QuoteHistory } from '../components/QuoteHistory.js';

/**
 * Kundenbereich: der Angebotsworkflow aus M4 bis M6, um den G-ST-P/OE-
 * Konfigurator ergaenzt (ADR-0022).
 *
 * Die gefuehrte Auswahl ist der primaere Weg und steht deshalb zuerst. Die
 * Freitextsuche ist der zweite Einstieg, der OE-Konfigurator der dritte -- alle
 * drei fuellen denselben Warenkorb.
 */
export function CustomerApp(): React.JSX.Element {
  const [tab, setTab] = useState<'guided' | 'search' | 'oe' | 'history'>('guided');
  const [positions, setPositions] = useState<readonly CartLine[]>([]);
  /** Zaehler, der die Historie nach dem Ausstellen neu laedt. */
  const [historyNonce, setHistoryNonce] = useState(0);
  /** Haelt das Angebotsfeld sichtbar, auch wenn die Positionsliste leer ist. */
  const [quoteOffen, setQuoteOffen] = useState(false);
  /**
   * Erzwingt einen kompletten Neuaufbau des OE-Konfigurators bei "Neues
   * Angebot beginnen" -- als `key` auf `OeConfigurator` unten. Der
   * Konfigurator haelt seine Eingaben (Masse, Werkstoff, Profil, ...) und
   * seine eigene Vorschau in eigenem State, der unabhaengig vom Warenkorb ist
   * und deshalb durch das Leeren von `positions` allein nicht zurueckgesetzt
   * wird. Ein React-Remount ist hier der korrekte, vollstaendige Reset --
   * kein manuelles Nachbauen jedes einzelnen internen Feldes von aussen.
   */
  const [oeResetNonce, setOeResetNonce] = useState(0);

  /** Gleicher Artikel zweimal uebernommen: Mengen werden addiert, nicht ersetzt. */
  function addPositions(neue: readonly SelectedPosition[]): void {
    setPositions((prev) => {
      const byArticle = new Map(
        prev
          .filter((p): p is SelectedPosition => 'articleId' in p)
          .map((p) => [p.articleId, p]),
      );
      for (const n of neue) {
        const vorhanden = byArticle.get(n.articleId);
        byArticle.set(
          n.articleId,
          vorhanden === undefined
            ? n
            : { ...n, quantity: String(Number(vorhanden.quantity) + Number(n.quantity)) },
        );
      }
      const oeZeilen = prev.filter((p): p is OeSealCartLine => !('articleId' in p));
      return [...byArticle.values(), ...oeZeilen];
    });
  }

  /** Jede konfigurierte Dichtung ist eine eigene Zeile -- anders als beim
   *  Katalogartikel gibt es keine Zusammenfuehrung ueber eine Artikelnummer. */
  function addOeLine(line: OeSealCartLine): void {
    setPositions((prev) => [...prev, line]);
  }

  return (
    <>
      <main className="shell wide">
        <div className="layout">
          <div>
            <nav className="tabs" aria-label="Einstieg">
              <button
                type="button"
                className={tab === 'guided' ? 'tab selected' : 'tab'}
                aria-current={tab === 'guided'}
                onClick={() => setTab('guided')}
              >
                Gefuehrte Auswahl
              </button>
              <button
                type="button"
                className={tab === 'search' ? 'tab selected' : 'tab'}
                aria-current={tab === 'search'}
                onClick={() => setTab('search')}
              >
                Freitextsuche
              </button>
              <button
                type="button"
                className={tab === 'oe' ? 'tab selected' : 'tab'}
                aria-current={tab === 'oe'}
                onClick={() => setTab('oe')}
              >
                G-ST-P/OE konfigurieren
              </button>
              <button
                type="button"
                className={tab === 'history' ? 'tab selected' : 'tab'}
                aria-current={tab === 'history'}
                onClick={() => setTab('history')}
              >
                Meine Angebote
              </button>
            </nav>

            {tab === 'guided' && <GuidedSelection onTakeOver={addPositions} />}
            {tab === 'search' && <FreeTextSearch onTakeOver={addPositions} />}
            {tab === 'oe' && <OeConfigurator key={oeResetNonce} onTakeOver={addOeLine} />}
            {tab === 'history' && <QuoteHistory nonce={historyNonce} />}

            {/*
              Das Angebotsfeld bleibt nach dem Ausstellen stehen -- `quoteOffen`
              haelt es sichtbar, obwohl der Warenkorb (unten) bereits geleert
              ist. Ein ausgestelltes Angebot ist abgeschlossen: Der Warenkorb
              wird direkt bei Erfolg geleert (siehe `onIssued`), nicht erst
              beim naechsten "Neues Angebot beginnen" -- sonst bliebe die
              rechte Spalte mit "Uebernommene Positionen" und einer
              "Angebotsvorschau" stehen, die zum bereits ausgestellten Angebot
              gehoerte, aber nicht mehr dazu gehoert.
            */}
            {tab !== 'history' && (positions.length > 0 || quoteOffen) && (
              <QuotePanel
                positions={positions}
                onQuoteChange={setQuoteOffen}
                onIssued={() => {
                  setHistoryNonce((n) => n + 1);
                  setPositions([]);
                }}
                onReset={() => {
                  setPositions([]);
                  setQuoteOffen(false);
                  setOeResetNonce((n) => n + 1);
                }}
              />
            )}
          </div>

          <SelectionList
            positions={positions}
            onRemove={(key) =>
              setPositions((prev) => prev.filter((p) => cartLineKey(p) !== key))
            }
            onClear={() => setPositions([])}
          />
        </div>
      </main>
    </>
  );
}
